import { Controller } from "@hotwired/stimulus"

// Selecting a job on the canvas (#269). One job at a time; the selection is
// what the people drawer follows. Lives on the canvas root; cards bind
// `click->canvas-job-selection#select` and carry the drawer's paths as data.
//
// Nothing here changes the cards' own markup: the selection is a ring on the
// card root, re-applied after Turbo morphs the canvas, because the server
// HTML never carries it.
// Styling lives in CSS, keyed on the data attribute below: a 2px ring with
// an offset gap was getting lost in the card's own border, and its offset
// colour was the panel's slate-700 rather than the board it actually sits on.
const SELECTED_CLASSES = []
const INTERACTIVE = "a, button, input, textarea, select, label, [contenteditable], [data-inline-edit-target='input']"

export default class extends Controller {
  // The surface shortcut and the explicit accessible button share selection.
  // Keeping the button out of the header's layout preserves the card width.
  select(event) {
    const card = event.currentTarget?.closest?.("[data-drawer-job-id]") || event.currentTarget
    if (!card) return
    // A click or Enter on a control inside the card is that control's
    // business: renaming, editing, the picker, a person chip.
    const toggle = event.target.closest("[data-job-selection-toggle]")
    if (!toggle && event.target.closest(INTERACTIVE)) return
    if (window.getSelection?.()?.toString()) return

    const wasSelected = card.hasAttribute("data-canvas-job-selected")
    this.clear()
    if (wasSelected) {
      window.dispatchEvent(new CustomEvent("canvas:job-deselected"))
      return
    }

    this.selectedJobId = card.dataset.drawerJobId
    this.#mark(card)
    this.#announce(card)
  }

  #announce(card, restored = false) {
    window.dispatchEvent(new CustomEvent("canvas:job-selected", {
      detail: {
        jobId: card.dataset.drawerJobId,
        jobName: card.dataset.drawerJobName,
        jobPeoplePath: card.dataset.drawerPeoplePath,
        drawerPath: card.dataset.drawerPath,
        decorationsPath: card.dataset.drawerDecorationsPath,
        mode: "people",
        restored
      }
    }))
  }

  keydown(event) {
    if (event.key !== "Escape" || !this.selectedJobId) return
    if (event.target.closest?.("input, textarea, select, [contenteditable]")) return

    this.clear()
    window.dispatchEvent(new CustomEvent("canvas:job-deselected"))
  }

  // Something else deselected (the drawer's own close control, a card that
  // vanished): forget the selection without announcing it again.
  clear() {
    this.selectedJobId = null
    this.element.querySelectorAll("[data-canvas-job-selected]").forEach(card => {
      card.removeAttribute("data-canvas-job-selected")
      card.classList.remove(...SELECTED_CLASSES)
      card.querySelector("[data-job-selection-toggle]")?.setAttribute("aria-pressed", "false")
    })
  }

  // After a morph the ring is gone and the card may be a new element.
  restore() {
    if (!this.selectedJobId) return

    const card = this.element.querySelector(`[data-drawer-job-id="${CSS.escape(this.selectedJobId)}"]`)
    if (card) {
      this.#mark(card)
      // Newly inserted Stimulus drawers connect after the morph callback.
      // Announce on the next frame so they can recover the selected scope;
      // surviving instances ignore this if they already hold the same job.
      cancelAnimationFrame(this.restoreFrame)
      this.restoreFrame = requestAnimationFrame(() => {
        if (card.isConnected && this.selectedJobId === card.dataset.drawerJobId) this.#announce(card, true)
      })
    } else {
      this.clear()
      window.dispatchEvent(new CustomEvent("canvas:job-deselected"))
    }
  }

  #mark(card) {
    card.setAttribute("data-canvas-job-selected", "")
    card.classList.add(...SELECTED_CLASSES)
    card.querySelector("[data-job-selection-toggle]")?.setAttribute("aria-pressed", "true")
  }

  disconnect() {
    cancelAnimationFrame(this.restoreFrame)
  }
}
